import React from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

const COLORS = {
  target: "#00e676",
  current: "#29b6f6",
  integrated: "#ef5350",
  forecast: "#ce93d8",
  geyser: "#ffb300",
};

const BG = "#090e1a";
const GRID = "#111827";

function glow(color) {
  return {
    shadowBlur: 12,
    shadowColor: color,
  };
}

function gradient(color) {
  return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: color + "88" },
    { offset: 0.5, color: color + "33" },
    { offset: 1, color: color + "05" },
  ]);
}

function toTs(items, valueFn) {
  return items.map((item) => [new Date(item.interval).getTime(), valueFn(item)]);
}

function baseOption(seriesList) {
  return {
    backgroundColor: BG,
    animation: false,
    grid: { top: 40, right: 80, bottom: 50, left: 60 },
    dataZoom: [{ type: "inside", xAxisIndex: 0, filterMode: "filter" }],
    toolbox: {
      right: 8, top: 4,
      feature: {
        dataZoom: { yAxisIndex: "none", title: { zoom: "Zoom", back: "Reset" }, iconStyle: { borderColor: "#4a6fa5" }, emphasis: { iconStyle: { borderColor: "#29b6f6" } } },
        restore: { title: "Reset", iconStyle: { borderColor: "#4a6fa5" }, emphasis: { iconStyle: { borderColor: "#29b6f6" } } },
      },
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: "#0d1526cc",
      borderColor: "#1e3a5f",
      extraCssText: "backdrop-filter: blur(8px); border-radius: 8px;",
      textStyle: { color: "#c9d4e8", fontSize: 12 },
      formatter: (params) =>
        `<div style="margin-bottom:4px;color:#4a6fa5;font-size:11px">${new Date(params[0].value[0]).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>` +
        params
          .filter((p) => p.value[1] !== null)
          .map(
            (p) =>
              `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:6px;box-shadow:0 0 6px ${p.color}"></span>` +
              `${p.seriesName}: <b>${Number(p.value[1]).toFixed(3)} MVA</b>`
          )
          .join("<br/>"),
    },
    legend: {
      top: 4,
      textStyle: { color: "#6a8ab0", fontSize: 11 },
      icon: "circle",
      itemWidth: 8,
      itemHeight: 8,
    },
    xAxis: {
      type: "time",
      axisLine: { lineStyle: { color: "#1a2a45" } },
      axisTick: { lineStyle: { color: "#1a2a45" } },
      axisLabel: { color: "#4a6fa5", fontSize: 10, formatter: (v) => {
        const d = new Date(v);
        return `${d.getHours().toString().padStart(2,"0")}:${d.getMinutes().toString().padStart(2,"0")}`;
      }},
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: "#4a6fa5", fontSize: 10, formatter: (v) => v.toFixed(2) },
      splitLine: { lineStyle: { color: GRID, type: "dashed" } },
    },
    series: seriesList,
  };
}

function glowLine(name, color, data, dashed = false) {
  return {
    name,
    type: "line",
    smooth: true,
    symbol: "none",
    lineStyle: { color, width: 2, type: dashed ? "dashed" : "solid", ...glow(color) },
    areaStyle: { color: gradient(color) },
    itemStyle: { color },
    data: data.filter(([, y]) => y !== null),
  };
}

function EmptyState() {
  return (
    <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography sx={{ color: "#4a6fa5" }}>No data available</Typography>
    </Box>
  );
}

export default function EChartsTemplate({ microServiceGraphData, targetDataNow, microserviceForcastState }) {
  const hasData = microServiceGraphData.length > 0;

  const demandSeries = [
    glowLine("Target Demand", COLORS.target,
      toTs(microServiceGraphData, () => targetDataNow?.target ? targetDataNow.target / 1000000 : null), true),
    glowLine("Current Demand", COLORS.current,
      toTs(microServiceGraphData, (i) => i.current_demand ? i.current_demand / 1000000 : null)),
    glowLine("Integrated Demand", COLORS.integrated,
      toTs(microServiceGraphData, (i) => i.latest_integrated_demand ? i.latest_integrated_demand / 1000000 : null)),
    glowLine("Forecast", COLORS.forecast,
      toTs(microServiceGraphData, () => microserviceForcastState?.[0]?.forecast ? microserviceForcastState[0].forecast / 1000000 : null), true),
  ];

  const geyserSeries = [
    {
      name: "Geyser Demand",
      type: "line",
      smooth: true,
      symbol: "none",
      lineStyle: { color: COLORS.geyser, width: 2, ...glow(COLORS.geyser) },
      areaStyle: { color: gradient(COLORS.geyser) },
      itemStyle: { color: COLORS.geyser },
      data: toTs(microServiceGraphData, (i) => i.geyser_demand ? i.geyser_demand / 1000000 : null).filter(([, y]) => y !== null),
    },
  ];

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ color: "#4a6fa5", mb: 1, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>
          Demand Overview — MVA
        </Typography>
        <Box sx={{ bgcolor: BG, borderRadius: 2, overflow: "hidden" }}>
          {hasData
            ? <ReactECharts option={baseOption(demandSeries)} style={{ height: 380 }} notMerge />
            : <Box sx={{ height: 380 }}><EmptyState /></Box>}
        </Box>
      </Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ color: "#4a6fa5", mb: 1, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>
          Geyser (Tjommie) Demand — MVA
        </Typography>
        <Box sx={{ bgcolor: BG, borderRadius: 2, overflow: "hidden" }}>
          {hasData
            ? <ReactECharts option={baseOption(geyserSeries)} style={{ height: 380 }} notMerge />
            : <Box sx={{ height: 380 }}><EmptyState /></Box>}
        </Box>
      </Box>
    </>
  );
}
